import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { getAdditionalSectionsFromContent, getBoxesFromContent, getBoxesSectionTitle } from '../../utils/servicePageBody';
import ServicePageBoxes from './ServicePageBoxes';

const RADIOLOGY_SLUGS = {
  'ct scan': 'ct-scan',
  'x-ray': 'x-ray',
  'x ray': 'x-ray',
  'ultra sound': 'ultrasound',
  ultrasound: 'ultrasound',
  mri: 'mri',
};

const getRadiologySlug = (title) => {
  const key = String(title || '').trim().toLowerCase();
  return RADIOLOGY_SLUGS[key] || null;
};

const FeatureGrid = ({ section }) => (
  <Paper sx={{ p: 6, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', mb: 8 }}>
    {section.title && (
      <Typography variant="h4" component="h2" gutterBottom textAlign="center" sx={{ mb: 6, fontWeight: 'bold' }}>
        {section.title}
      </Typography>
    )}
    <Grid container spacing={4}>
      {section.items.map((item, index) => (
        <Grid item xs={12} md={4} key={`${item.title}-${index}`}>
          <Box sx={{ textAlign: 'center' }}>
            <LocalHospitalIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {item.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.text}
            </Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  </Paper>
);

const DocumentsSection = ({ section }) => (
  <Paper sx={{ p: 6, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', mb: 8 }}>
    {section.title && (
      <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
        {section.title}
      </Typography>
    )}
    <Grid container spacing={3}>
      {section.items.map((item, index) => (
        <Grid item xs={12} md={6} key={`${item.title}-${index}`}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              {item.title}
            </Typography>
            {item.listItems?.length > 0 && (
              <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
                {item.listItems.map((li, liIndex) => (
                  <li key={`${li}-${liIndex}`}>{li}</li>
                ))}
              </Typography>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  </Paper>
);

const CardsSection = ({ section, clickableArticles = false, onArticleClick }) => (
  <Box sx={{ mb: 8 }}>
    {section.title && (
      <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
        {section.title}
      </Typography>
    )}
    <Grid container spacing={4}>
      {section.items.map((item, index) => {
        const slug = clickableArticles ? getRadiologySlug(item.title) : null;
        const isClickable = Boolean(slug && onArticleClick);

        return (
          <Grid item xs={12} md={6} lg={section.items.length === 4 ? 3 : 4} key={`${item.title}-${index}`}>
            <Card
              sx={{
                height: '100%',
                textAlign: section.type === 'cards' && !item.listItems?.length ? 'center' : 'left',
                p: section.type === 'cards' && !item.listItems?.length ? 3 : 0,
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                '&:hover': isClickable ? { transform: 'translateY(-8px)', boxShadow: 6 } : undefined,
              }}
              onClick={isClickable ? () => onArticleClick(slug) : undefined}
            >
              <CardContent sx={{ p: section.type === 'cards' && !item.listItems?.length ? 0 : 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                  {item.title}
                </Typography>
                {item.text && (
                  <Typography variant="body2" color="text.secondary" paragraph={Boolean(item.listItems?.length)}>
                    {item.text}
                  </Typography>
                )}
                {item.listItems?.length > 0 && (
                  <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
                    {item.listItems.map((li, liIndex) => (
                      <li key={`${li}-${liIndex}`}>{li}</li>
                    ))}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  </Box>
);

const ListSection = ({ section }) => (
  <Box sx={{ mb: 8 }}>
    {section.title && (
      <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 4, textAlign: 'center' }}>
        {section.title}
      </Typography>
    )}
    <Paper sx={{ p: 4, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, m: 0 }}>
        {section.listItems.map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </Typography>
    </Paper>
  </Box>
);

const CtaSection = ({ section, variant = 'default' }) => {
  const paragraphs = section.paragraphs?.length
    ? section.paragraphs
    : section.items.flatMap((item) => [item.title, item.text].filter(Boolean));

  const ctaLabel = paragraphs.find((p) => p.length < 40) || '';
  const bodyParagraphs = paragraphs.filter((p) => p !== ctaLabel);

  if (variant === 'emergency') {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Card sx={{
          background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
          color: 'white',
          textAlign: 'center',
          py: 6,
        }}
        >
          <CardContent>
            <LocalHospitalIcon sx={{ fontSize: 64, mb: 2 }} />
            {section.title && (
              <Typography variant="h4" component="h2" gutterBottom>
                {section.title}
              </Typography>
            )}
            {bodyParagraphs.map((paragraph, index) => (
              <Typography key={`${paragraph}-${index}`} variant={index === 0 ? 'h6' : 'body1'} sx={{ mb: index === bodyParagraphs.length - 1 ? 3 : 2, opacity: 0.9 }}>
                {paragraph}
              </Typography>
            ))}
            {ctaLabel && (
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {ctaLabel}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (variant === 'primary') {
    return (
      <Box sx={{ bgcolor: '#1976d2', color: 'white', py: 8 }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          {section.title && (
            <Typography variant="h4" component="h2" gutterBottom>
              {section.title}
            </Typography>
          )}
          {bodyParagraphs.map((paragraph, index) => (
            <Typography key={`${paragraph}-${index}`} variant={index === 0 ? 'h6' : 'body1'} sx={{ mb: 2, opacity: index === 0 ? 0.9 : 1 }}>
              {paragraph}
            </Typography>
          ))}
          {ctaLabel && (
            <Chip label={ctaLabel} color="primary" size="large" sx={{ fontSize: '1.1rem', py: 1, mt: 2, bgcolor: 'white', color: 'primary.main' }} />
          )}
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 8, p: 6, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, textAlign: 'center' }}>
      {section.title && (
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
          {section.title}
        </Typography>
      )}
      {bodyParagraphs.map((paragraph, index) => (
        <Typography key={`${paragraph}-${index}`} variant="body1" paragraph={index < bodyParagraphs.length - 1} sx={{ fontSize: '1.1rem', lineHeight: 1.8, maxWidth: 600, mx: 'auto' }}>
          {paragraph}
        </Typography>
      ))}
      {ctaLabel && (
        <Chip label={ctaLabel} color="primary" size="large" sx={{ fontSize: '1.1rem', py: 1 }} />
      )}
    </Box>
  );
};

const getCtaVariant = (section, globalVariant) => {
  if (globalVariant !== 'default') return globalVariant;
  const title = String(section.title || '').toLowerCase();
  if (title.includes('emergency')) return 'emergency';
  if (title.includes('schedule') || title.includes('appointment')) return 'primary';
  return 'default';
};

const renderSection = (section, index, options) => {
  const { clickableArticles, onArticleClick, ctaVariant } = options;
  const resolvedCtaVariant = getCtaVariant(section, ctaVariant);

  switch (section.type) {
    case 'features':
      return <FeatureGrid key={`section-${index}`} section={section} />;
    case 'documents':
      return <DocumentsSection key={`section-${index}`} section={section} />;
    case 'list':
      return <ListSection key={`section-${index}`} section={section} />;
    case 'cta':
      return <CtaSection key={`section-${index}`} section={section} variant={resolvedCtaVariant} />;
    default:
      return (
        <CardsSection
          key={`section-${index}`}
          section={section}
          clickableArticles={clickableArticles}
          onArticleClick={onArticleClick}
        />
      );
  }
};

const ServicePageBody = ({
  content,
  bodyHtml,
  sections: sectionsProp,
  boxes: boxesProp,
  boxesSectionTitle: boxesSectionTitleProp,
  inContainer = true,
  clickableArticles = false,
  onArticleClick,
  ctaVariant = 'default',
  greyBackgroundIndexes = [],
  greyBoxesBackground = false,
  showBoxes = true,
}) => {
  const boxes = boxesProp ?? getBoxesFromContent(content || { bodyHtml });
  const boxesSectionTitle = boxesSectionTitleProp ?? getBoxesSectionTitle(content || { bodyHtml });
  const sections = sectionsProp?.length
    ? sectionsProp
    : getAdditionalSectionsFromContent(content || { bodyHtml });

  const hasBoxes = showBoxes && boxes.some((box) => box.title || box.text);
  const hasSections = sections.length > 0;

  if (!hasBoxes && !hasSections) return null;

  const body = (
    <>
      {hasBoxes && (
        <ServicePageBoxes
          boxesSectionTitle={boxesSectionTitle}
          boxes={boxes}
          greyBackground={greyBoxesBackground}
          clickable={clickableArticles}
          onBoxClick={(_index, title) => {
            const slug = getRadiologySlug(title);
            if (slug && onArticleClick) onArticleClick(slug);
          }}
        />
      )}
      {hasSections && sections.map((section, index) => {
        const sectionNode = renderSection(section, index, { clickableArticles, onArticleClick, ctaVariant });

        if (greyBackgroundIndexes.includes(index)) {
          return (
            <Box key={`wrap-${index}`} sx={{ bgcolor: '#f5f5f5', py: 8 }}>
              <Container maxWidth="lg">
                {sectionNode}
              </Container>
            </Box>
          );
        }

        return sectionNode;
      })}
    </>
  );

  if (inContainer) {
    return <Container maxWidth="lg" sx={{ py: 0 }}>{body}</Container>;
  }

  return <>{body}</>;
};

export default ServicePageBody;
